/**
 * `/events` — the met-at spine.
 *
 * The search and date filters that used to be a placard are wired; see
 * `event-filter-bar.tsx` for what the server actually honours and why `kind` is
 * single-select.
 */

import { Badge, Button, EmptyState, SkeletonRows } from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { CalendarDaysIcon } from 'lucide-react';
import { useState } from 'react';
import { useEventsQuery } from '../../../api/queries.ts';
import { CreateEventDialog } from '../../../components/events/create-event-dialog.tsx';
import {
  EventFilterBar,
  useEventFilterNavigation,
} from '../../../components/events/event-filter-bar.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { formatDateTime } from '../../../lib/format.ts';
import { parseEventsSearch } from '../../../lib/search.ts';

export const Route = createFileRoute('/_authed/events/')({
  validateSearch: parseEventsSearch,
  component: EventsPage,
  errorComponent: RouteErrorPanel,
});

function EventsPage() {
  const search = Route.useSearch();
  const onChange = useEventFilterNavigation(search);
  const [creating, setCreating] = useState(false);
  const events = useEventsQuery(search);
  const items = events.data?.items ?? [];
  const filtered =
    search.q !== undefined ||
    search.kind !== undefined ||
    search.from !== undefined ||
    search.to !== undefined;

  return (
    <>
      <PageHeader
        title="Events"
        description="Conferences, dinners and meetings you met people at."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            New event
          </Button>
        }
      />

      <EventFilterBar value={search} onChange={onChange} />

      <PageBody>
        {events.isLoading ? (
          <SkeletonRows rows={5} />
        ) : events.isError ? (
          <EmptyState kind="error" title="Could not load events" />
        ) : items.length === 0 && filtered ? (
          <EmptyState
            kind="filtered"
            title="No events match these filters"
            description="Your events are still there — this view just excludes all of them."
            action={
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  onChange({ q: undefined, kind: undefined, from: undefined, to: undefined })
                }
              >
                Clear filters
              </Button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<CalendarDaysIcon aria-hidden />}
            title="No events yet"
            description="An event is created when you add a contact at one, when calendar sync promotes a meeting, or right here."
            action={
              <Button size="sm" onClick={() => setCreating(true)}>
                Add your first event
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {items.map((event) => (
              <li key={event.id}>
                {/* Stacked below `sm`: name and badges on line one, date and
                    headcount on line two. The single-row version put four
                    variable-width things on a 390px line and truncated the name
                    — the only part worth reading. */}
                <Link
                  to="/events/$eventId"
                  params={{ eventId: event.id }}
                  className="flex min-h-11 flex-col justify-center gap-1 px-4 py-2.5 text-sm hover:bg-accent/50 sm:flex-row sm:items-center sm:gap-3"
                >
                  <span className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{event.name}</span>
                    <Badge variant="muted">{event.kind.replace('_', ' ')}</Badge>
                    {event.source === 'calendar' ? (
                      <Badge variant="secondary">calendar</Badge>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground sm:ml-auto sm:shrink-0">
                    {formatDateTime(event.startsAt) ?? 'no date'} · {event.contactCount} people
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <CreateEventDialog open={creating} onOpenChange={setCreating} />
      </PageBody>
    </>
  );
}
